/**
 * Value-input widgets (Tailwind, brand-matched). The widget is chosen by the
 * matrix's `value_widget` for each key.
 */
import React, { useEffect, useRef, useState } from "react";
import { useBuilder } from "./BuilderContext";
import { Select, TextInput, Chip, cx } from "./ui";
import {
  useLazyGetCatalogProductsQuery,
  useLazyGetCatalogCategoriesQuery,
  useLazyGetCatalogBrandsQuery,
} from "../../../../features/tenant/rules/rulesApi";

const useDebounced = (value, ms = 250) => {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
};

/** Wallet picker: Main wallet + active schemes. Writes 'default' or scheme id. */
export function WalletPicker({ value, onChange, error }) {
  const { wallets } = useBuilder();
  const valStr = value != null ? String(value) : "default";
  const exists = valStr === "default" || (wallets || []).some((w) => String(w.scheme_id) === valStr);

  const options = [
    { value: "default", label: "Main wallet (user points)" },
    ...(wallets || []).map((w) => ({ value: String(w.scheme_id), label: `${w.name} · scheme wallet` })),
  ];

  if (!exists) {
    options.push({
      value: valStr,
      label: `⚠️ Invalid / Deleted Scheme Wallet (ID: ${valStr})`,
    });
  }

  return <Select value={valStr} onChange={onChange} options={options} error={error} />;
}

/** Scheme picker — shows names, writes ids. Single (with '*') or multi. */
export function SchemePicker({ value, multiple, onChange, error }) {
  const { wallets } = useBuilder();
  const opts = (wallets || []).map((w) => ({ value: String(w.scheme_id), label: w.name }));

  if (!multiple) {
    const valStr = value != null ? String(value) : "";
    const exists = !valStr || valStr === "*" || opts.some((o) => o.value === valStr);
    const allOpts = [{ value: "*", label: "Any scheme (*)" }, ...opts];
    if (!exists) {
      allOpts.push({ value: valStr, label: `⚠️ Invalid / Deleted Scheme (ID: ${valStr})` });
    }
    return (
      <Select
        value={valStr}
        onChange={onChange}
        error={error}
        placeholder="Select a scheme"
        options={allOpts}
      />
    );
  }
  const selected = (Array.isArray(value) ? value : []).map(String);
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div className={cx("rounded-lg border p-2", error ? "border-red-300" : "border-gray-300")}>
      <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
        {opts.map((o) => (
          <button key={o.value} type="button" onClick={() => toggle(o.value)}
            className={cx("px-2.5 py-1 rounded-full text-xs font-medium border transition-colors shrink-0",
              selected.includes(o.value) ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50")}>
            {o.label}
          </button>
        ))}
        {selected.filter((s) => !opts.some((o) => o.value === s)).map((deletedId) => (
          <button key={deletedId} type="button" onClick={() => toggle(deletedId)}
            className="px-2.5 py-1 rounded-full text-xs font-medium border bg-rose-100 text-rose-700 border-rose-300">
            ⚠️ Deleted Scheme ({deletedId})
          </button>
        ))}
        {opts.length === 0 && selected.length === 0 && <span className="text-xs text-gray-400">No active schemes</span>}
      </div>
    </div>
  );
}

/** Catalog combobox for product_code / category / brand (single or list). */
export function CatalogAutocomplete({ catalog, multiple, value, onChange, placeholder, error }) {
  const { token, tenantId } = useBuilder();
  const [input, setInput] = useState("");
  const [open, setOpen] = useState(false);
  const search = useDebounced(input);
  const boxRef = useRef(null);

  const [trigProducts, prodRes] = useLazyGetCatalogProductsQuery();
  const [trigCats, catRes] = useLazyGetCatalogCategoriesQuery();
  const [trigBrands, brandRes] = useLazyGetCatalogBrandsQuery();
  const trigger = catalog === "products" ? trigProducts : catalog === "categories" ? trigCats : trigBrands;
  const res = catalog === "products" ? prodRes : catalog === "categories" ? catRes : brandRes;

  useEffect(() => { if (open) trigger({ token, tenant_id: tenantId, search }); }, [search, open]); // eslint-disable-line
  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const val = (row) => catalog === "products" ? row.product_code : catalog === "categories" ? row.category_name : row.brand_name;
  const lbl = (row) => catalog === "products" ? `${row.product_code} — ${row.name || ""}` : val(row);
  const rows = res?.data || [];

  const pick = (v) => {
    if (multiple) { const arr = Array.isArray(value) ? value : []; if (!arr.includes(v)) onChange([...arr, v]); }
    else { onChange(v); setInput(""); setOpen(false); }
  };
  const selected = multiple ? (Array.isArray(value) ? value : []) : [];

  return (
    <div className="relative" ref={boxRef}>
      {multiple && selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-1.5">
          {selected.map((v) => (
            <Chip key={v} color="blue">
              {v}
              <button type="button" className="ml-1 text-blue-500 hover:text-blue-800" onClick={() => onChange(selected.filter((x) => x !== v))}>×</button>
            </Chip>
          ))}
        </div>
      )}
      <TextInput
        value={multiple ? input : (open ? input : (value || ""))}
        placeholder={placeholder || `Search ${catalog}…`}
        error={error}
        onChange={(e) => { setInput(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
      />
      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-56 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick("*")} className="block w-full text-left px-3 py-2 text-sm hover:bg-blue-50 text-blue-700 font-medium">Any (*)</button>
          {res?.isFetching && <div className="px-3 py-2 text-sm text-gray-400">Searching…</div>}
          {rows.map((r) => (
            <button key={val(r)} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(val(r))}
              className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 text-gray-700">{lbl(r)}</button>
          ))}
          {!res?.isFetching && rows.length === 0 && <div className="px-3 py-2 text-sm text-gray-400">No matches — type an exact value; it's validated on save.</div>}
        </div>
      )}
    </div>
  );
}

/** Chip/tag list input for free string lists (e.g. all_of items). */
export function ChipListInput({ value, onChange, placeholder, error }) {
  const [input, setInput] = useState("");
  const items = Array.isArray(value) ? value : [];
  const add = () => { const v = input.trim(); if (v && !items.includes(v)) onChange([...items, v]); setInput(""); };
  return (
    <div>
      <TextInput value={input} placeholder={placeholder || "Type and press Enter"} error={error}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }}
        onBlur={add} />
      {items.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {items.map((it) => (
            <Chip key={it} color="blue">{it}
              <button type="button" className="ml-1 text-blue-500 hover:text-blue-800" onClick={() => onChange(items.filter((x) => x !== it))}>×</button>
            </Chip>
          ))}
        </div>
      )}
    </div>
  );
}
